import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Search,
  BarChart2,
  BookOpen,
  Award,
  Edit3,
  Video,
  Settings,
  UserPlus,
} from 'lucide-react';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { QuickActionFeatured } from './QuickActionFeatured';
import { QuickActionItem } from './QuickActionItem';
import { QuickActionSection } from './QuickActionSection';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { useInstructor } from '@/features/profile/hooks/useInstructor';
import { useInstructorReviews } from '@/features/roll-review/hooks/useInstructorReviews';

// ── Instructor Hub ─────────────────────────────────────────────────────────

const InstructorHub: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const userId = user?.id;
  const { pendingCount } = useInstructorReviews();

  const pendingDesc = pendingCount > 0
    ? t('quick_actions.instructor.pending_reviews_desc', { count: pendingCount })
    : t('quick_actions.instructor.no_pending_reviews_desc');

  return (
    <>
      {/* Pending Video Reviews featured */}
      <div className="mb-6">
        <QuickActionFeatured
          icon={<Video className="text-sossiety-red" size={28} />}
          title={t('quick_actions.instructor.featured_title')}
          description={pendingDesc}
          onClick={() => navigate('/my-roll-reviews')}
        />
      </div>

      {/* MANAGE CLASSES */}
      <QuickActionSection title={t('quick_actions.instructor.section_manage')}>
        <QuickActionItem
          icon={<UserPlus size={18} />}
          title={t('quick_actions.instructor.class_requests')}
          description={t('quick_actions.instructor.class_requests_desc')}
          onClick={() => navigate('/calendar', { state: { activeTab: 'classes' } })}
        />
        <QuickActionItem
          icon={<Video size={18} />}
          title={t('quick_actions.instructor.video_reviews')}
          description={t('quick_actions.instructor.video_reviews_desc')}
          onClick={() => navigate('/my-roll-reviews')}
        />
      </QuickActionSection>

      {/* MY PRESENCE */}
      <QuickActionSection title={t('quick_actions.instructor.section_presence')}>
        <QuickActionItem
          icon={<Calendar size={18} />}
          title={t('quick_actions.instructor.my_agenda')}
          description={t('quick_actions.instructor.my_agenda_desc')}
          onClick={() => navigate('/settings/instructor-tools/schedule')}
        />
        <QuickActionItem
          icon={<Settings size={18} />}
          title={t('quick_actions.instructor.instructor_settings')}
          description={t('quick_actions.instructor.instructor_settings_desc')}
          onClick={() => navigate('/settings/instructor-tools')}
        />
      </QuickActionSection>

      {/* COMMUNITY */}
      <QuickActionSection title={t('quick_actions.community')}>
        <QuickActionItem
          icon={<Edit3 size={18} />}
          title={t('quick_actions.create_post')}
          description={t('quick_actions.create_post_desc')}
          onClick={() => navigate('/social')}
        />
        <QuickActionItem
          icon={<Search size={18} />}
          title={t('quick_actions.instructor.find_students')}
          description={t('quick_actions.instructor.find_students_desc')}
          onClick={() => navigate('/search')}
        />
      </QuickActionSection>

      {/* YOUR JOURNEY */}
      <QuickActionSection title={t('quick_actions.my_journey')}>
        <QuickActionItem
          icon={<BookOpen size={18} />}
          title={t('quick_actions.training_notes')}
          description={t('quick_actions.training_notes_desc')}
          onClick={() => navigate('/calendar', { state: { activeTab: 'notes' } })}
        />
        <QuickActionItem
          icon={<BarChart2 size={18} />}
          title={t('quick_actions.statistics')}
          description={t('quick_actions.statistics_desc')}
          onClick={() => userId ? navigate(`/user/statistics/${userId}`) : navigate('/calendar')}
        />
        <QuickActionItem
          icon={<Award size={18} />}
          title={t('quick_actions.achievements')}
          description={t('quick_actions.achievements_desc')}
          onClick={() => userId ? navigate(`/user/achievements/${userId}`) : navigate('/profile')}
        />
      </QuickActionSection>
    </>
  );
};

// ── Practitioner Hub ───────────────────────────────────────────────────────

const PractitionerHub: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const userId = user?.id;

  return (
    <>
      {/* Roll Review featured */}
      <div className="mb-6">
        <QuickActionFeatured
          icon={<Video className="text-sossiety-red" size={28} />}
          title={t('quick_actions.roll_review.title')}
          description={t('quick_actions.roll_review.description')}
          onClick={() => navigate('/browse-instructors?intent=video_review')}
        />
      </div>

      {/* GET IN TOUCH WITH OUR INSTRUCTORS */}
      <QuickActionSection title={t('quick_actions.train_with_instructor')}>
        <QuickActionItem
          icon={<Calendar size={18} />}
          title={t('quick_actions.schedule_class')}
          description={t('quick_actions.schedule_class_desc')}
          onClick={() => navigate('/browse-instructors?intent=private_classes')}
        />
        {/* Split a Class — hidden until feature is ready
        <QuickActionItem
          icon={<Users size={18} />}
          title={t('quick_actions.split_class')}
          description={t('quick_actions.split_class_desc')}
          onClick={() => navigate('/browse-instructors')}
        /> */}
        <QuickActionItem
          icon={<Video size={18} />}
          title={t('quick_actions.my_reviews')}
          description={t('quick_actions.my_reviews_desc')}
          onClick={() => navigate('/my-reviews')}
        />
      </QuickActionSection>

      {/* COMMUNITY */}
      <QuickActionSection title={t('quick_actions.community')}>
        <QuickActionItem
          icon={<Edit3 size={18} />}
          title={t('quick_actions.create_post')}
          description={t('quick_actions.create_post_desc')}
          onClick={() => navigate('/social')}
        />
        <QuickActionItem
          icon={<Search size={18} />}
          title={t('quick_actions.find_instructors')}
          description={t('quick_actions.find_instructors_desc')}
          onClick={() => navigate('/browse-instructors')}
        />
      </QuickActionSection>

      {/* YOUR JOURNEY */}
      <QuickActionSection title={t('quick_actions.my_journey')}>
        <QuickActionItem
          icon={<BookOpen size={18} />}
          title={t('quick_actions.training_notes')}
          description={t('quick_actions.training_notes_desc')}
          onClick={() => navigate('/calendar', { state: { activeTab: 'notes' } })}
        />
        <QuickActionItem
          icon={<BarChart2 size={18} />}
          title={t('quick_actions.statistics')}
          description={t('quick_actions.statistics_desc')}
          onClick={() => userId ? navigate(`/user/statistics/${userId}`) : navigate('/calendar')}
        />
        <QuickActionItem
          icon={<Award size={18} />}
          title={t('quick_actions.achievements')}
          description={t('quick_actions.achievements_desc')}
          onClick={() => userId ? navigate(`/user/achievements/${userId}`) : navigate('/profile')}
        />
      </QuickActionSection>
    </>
  );
};

// ── Page ───────────────────────────────────────────────────────────────────

const QuickActions: React.FC = () => {
  const { t } = useI18n();
  const { isInstructor, isLoading: roleLoading } = useInstructor();

  return (
    <>
      <TopNavigation variant="subtle" />

      <main
        className="min-h-screen text-white pt-20 pb-28 px-4"
        style={{ maxWidth: '100vw' }}
      >
        <div className="max-w-lg mx-auto">
          <h1 className="text-2xl font-bold text-white mb-1">{t('quick_actions.title')}</h1>
          <p className="text-white/40 text-sm mb-6">{t('quick_actions.subtitle')}</p>

          {roleLoading ? (
            <div className="flex flex-col gap-3">
              <div className="h-24 bg-white/5 rounded-2xl animate-pulse" />
              <div className="h-16 bg-white/5 rounded-xl animate-pulse" />
              <div className="h-16 bg-white/5 rounded-xl animate-pulse" />
            </div>
          ) : isInstructor ? (
            <InstructorHub />
          ) : (
            <PractitionerHub />
          )}
        </div>
      </main>

      <BottomNavigation />
    </>
  );
};

export default QuickActions;
